/**
 * Declarations Tab - Manage Declarations
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  LinearProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon, FactCheck as ComplianceIcon, Receipt as InvoiceIcon, CheckCircle as ConfirmIcon, DoneAll as BulkConfirmIcon, MoveToInbox as ReceivedIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import UploadIcon from '@mui/icons-material/Upload';
import HistoryIcon from '@mui/icons-material/History';
import EmailIcon from '@mui/icons-material/Email';

import { brand } from '../../../theme';
import {
  getDeclarations,
  searchDeclarations,
  bulkDeleteDeclarations,
  confirmDeclarationData,
  sendRenewalEmail,
  bulkConfirmDeclarations,
  bulkSendRenewalEmails,
  markDeclarationReceived,
  Declaration,
  ExtractionResult,
  SearchResult,
} from '../../../services/supplierDeclarationService';
import FileUploadModal from './FileUploadModal';
import ExtractionResultModal from './ExtractionResultModal';
import SearchBar from './SearchBar';
import SearchResults from './SearchResults';
import VersionHistoryModal from './VersionHistoryModal';
import ComplianceCheckPanel from './ComplianceCheckPanel';
import InvoiceAnalysisModal from './InvoiceAnalysisModal';

const DeclarationsTab: React.FC = () => {
  const [declarations, setDeclarations] = useState<Declaration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [extractionModalOpen, setExtractionModalOpen] = useState(false);
  const [selectedDeclarationId, setSelectedDeclarationId] = useState<string | null>(null);
  const [extractionResult, setExtractionResult] = useState<ExtractionResult | null>(null);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [complianceDecl, setComplianceDecl] = useState<Declaration | null>(null);
  const [invoiceAnalysisOpen, setInvoiceAnalysisOpen] = useState(false);
  const [selectedHistoryDeclarationId, setSelectedHistoryDeclarationId] = useState<string | null>(null);

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [renewingId, setRenewingId] = useState<string | null>(null);
  const [bulkConfirming, setBulkConfirming] = useState(false);
  const [bulkRenewing, setBulkRenewing] = useState(false);
  const [receivingId, setReceivingId] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async (silent = false) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const declData = await getDeclarations();
      setDeclarations(declData);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'expiring_soon': return 'warning';
      case 'expired': return 'error';
      default: return 'default';
    }
  };

  const getStatusChipSx = (status: string) => {
    if (status === 'requested') {
      return { backgroundColor: '#FFF9C4', color: '#7A5B00', fontWeight: 600 };
    }
    return undefined;
  };

  const actionBtnSx = {
    px: 1,
    py: 0.25,
    minWidth: 'auto',
    fontSize: 11,
    lineHeight: 1.4,
    '& .MuiButton-startIcon': { marginRight: 0.5, marginLeft: -0.25 },
    '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: 14 },
  };

  const handleUploadClick = (declarationId: string) => {
    setSelectedDeclarationId(declarationId);
    setUploadModalOpen(true);
  };

  const handleUploadSuccess = (result: ExtractionResult) => {
    setExtractionResult(result);
    setUploadModalOpen(false);
    setExtractionModalOpen(true);
    toast.success('Document processed successfully');
  };

  const handleConfirmSuccess = () => {
    setExtractionModalOpen(false);
    setExtractionResult(null);
    setSelectedDeclarationId(null);
    toast.success('Declaration data confirmed successfully');
    loadData(true);
  };

  const handleUploadModalClose = () => {
    setUploadModalOpen(false);
    setSelectedDeclarationId(null);
  };

  const handleExtractionModalClose = () => {
    setExtractionModalOpen(false);
    setExtractionResult(null);
    setSelectedDeclarationId(null);
  };

  const handleViewHistory = (declarationId: string) => {
    setSelectedHistoryDeclarationId(declarationId);
    setHistoryModalOpen(true);
  };

  const handleHistoryModalClose = () => {
    setHistoryModalOpen(false);
    setSelectedHistoryDeclarationId(null);
  };

  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    if (!query || query.trim() === '') {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    try {
      const results = await searchDeclarations(query);
      setSearchResults(results);
    } catch (err: any) {
      toast.error('Search failed. Please try again.');
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleDirectConfirm = async (declarationId: string) => {
    setConfirmingId(declarationId);
    try {
      const updated = await confirmDeclarationData(declarationId, {});
      toast.success('Declaration confirmed and set to Active.');
      setDeclarations((prev: Declaration[]) => prev.map(d => d.id === declarationId ? updated : d));
      loadData(true);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Confirmation failed');
    } finally {
      setConfirmingId(null);
    }
  };

  const handleSendRenewal = async (declarationId: string) => {
    setRenewingId(declarationId);
    try {
      await sendRenewalEmail(declarationId);
      toast.success('Renewal request sent to supplier.');
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to send renewal email');
    } finally {
      setRenewingId(null);
    }
  };

  const handleBulkConfirm = async () => {
    setBulkConfirming(true);
    const ids = Array.from(selectedIds) as string[];
    try {
      const result = await bulkConfirmDeclarations(ids);
      toast.success(`${result.confirmed} declaration(s) confirmed and set to Active.`);
      setSelectedIds(new Set());
      setDeclarations((prev: Declaration[]) =>
        prev.map(d => ids.indexOf(d.id) !== -1 ? { ...d, status: 'active' } : d)
      );
      loadData(true);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Bulk confirm failed');
    } finally {
      setBulkConfirming(false);
    }
  };

  const handleBulkSendRenewal = async () => {
    const renewableIds = (Array.from(selectedIds) as string[]).filter(id => {
      const decl = declarations.find((d: Declaration) => d.id === id);
      return decl && (decl.status === 'expired' || decl.status === 'expiring_soon');
    });
    if (renewableIds.length === 0) {
      toast.error('No expired or expiring declarations selected.');
      return;
    }
    setBulkRenewing(true);
    try {
      const result = await bulkSendRenewalEmails(renewableIds);
      if (result.sent > 0) toast.success(`Renewal email sent to ${result.sent} supplier(s).`);
      if (result.failed > 0) toast.warning(`${result.failed} email(s) failed — no valid address.`);
      setDeclarations((prev: Declaration[]) =>
        prev.map(d => renewableIds.indexOf(d.id) !== -1 ? { ...d, status: 'requested' } : d)
      );
      loadData(true);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Bulk renewal failed');
    } finally {
      setBulkRenewing(false);
    }
  };

  const handleMarkReceived = async (declarationId: string) => {
    setReceivingId(declarationId);
    try {
      const updated = await markDeclarationReceived(declarationId);
      toast.success('Marked as received. Ready to confirm.');
      setDeclarations((prev: Declaration[]) => prev.map(d => d.id === declarationId ? updated : d));
      loadData(true);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to mark as received');
    } finally {
      setReceivingId(null);
    }
  };

  // Selection handlers
  const isAllSelected = declarations.length > 0 && selectedIds.size === declarations.length;
  const isIndeterminate = selectedIds.size > 0 && selectedIds.size < declarations.length;

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(declarations.map((d: Declaration) => d.id)));
    }
  };

  const handleSelectOne = (id: string) => {
    setSelectedIds((prev: Set<string>) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleDeleteSelected = async () => {
    setDeleting(true);
    try {
      const result = await bulkDeleteDeclarations(Array.from(selectedIds));
      toast.success(`${result.deleted_count} declaration(s) deleted`);
      setSelectedIds(new Set());
      setDeleteConfirmOpen(false);
      loadData(true);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to delete declarations');
    } finally {
      setDeleting(false);
    }
  };

  const isSearchActive = searchQuery.trim() !== '';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, gap: 2 }}>
        <Box sx={{ flexGrow: 1, maxWidth: 600 }}>
          <SearchBar
            onSearch={handleSearch}
            loading={searching}
            placeholder="Search declarations by product, HS code, origin, or supplier..."
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {selectedIds.size > 0 && (() => {
            const renewableCount = (Array.from(selectedIds) as string[]).filter(id => {
              const d = declarations.find((x: Declaration) => x.id === id);
              return d && (d.status === 'expired' || d.status === 'expiring_soon');
            }).length;
            const pendingCount = (Array.from(selectedIds) as string[]).filter(id => {
              const d = declarations.find((x: Declaration) => x.id === id);
              return d && d.status === 'pending';
            }).length;
            return (
              <>
                {pendingCount > 0 && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={bulkConfirming ? <CircularProgress size={18} color="inherit" /> : <BulkConfirmIcon />}
                    onClick={handleBulkConfirm}
                    disabled={bulkConfirming}
                  >
                    {bulkConfirming ? 'Confirming...' : `Confirm Pending (${pendingCount})`}
                  </Button>
                )}
                {renewableCount > 0 && (
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={bulkRenewing ? <CircularProgress size={18} color="inherit" /> : <EmailIcon />}
                    onClick={handleBulkSendRenewal}
                    disabled={bulkRenewing}
                  >
                    {bulkRenewing ? 'Sending...' : `Send Renewal (${renewableCount})`}
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setDeleteConfirmOpen(true)}
                >
                  Delete ({selectedIds.size})
                </Button>
              </>
            );
          })()}
          <Button
            variant="outlined"
            startIcon={<InvoiceIcon />}
            onClick={() => setInvoiceAnalysisOpen(true)}
            sx={{ borderColor: brand.orange, color: brand.orange }}
          >
            Analyze Invoice
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {isSearchActive && (
        <Box sx={{ mb: 2 }}>
          <Button variant="outlined" size="small" onClick={handleClearSearch}>
            Clear Search
          </Button>
        </Box>
      )}

      {refreshing && <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />}

      {isSearchActive ? (
        <SearchResults
          results={searchResults}
          loading={searching}
          query={searchQuery}
          onViewDetails={(id: string) => {
            console.log('View details for declaration:', id);
          }}
        />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isAllSelected}
                    indeterminate={isIndeterminate}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Supplier</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Product</TableCell>
                <TableCell>HS Code</TableCell>
                <TableCell>Origin</TableCell>
                <TableCell>Valid Until</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {declarations.map((decl: Declaration) => (
                <TableRow
                  key={decl.id}
                  selected={selectedIds.has(decl.id)}
                  sx={{ '&.Mui-selected': { backgroundColor: 'action.selected' } }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedIds.has(decl.id)}
                      onChange={() => handleSelectOne(decl.id)}
                    />
                  </TableCell>
                  <TableCell>{decl.supplier_name}</TableCell>
                  <TableCell>{decl.declaration_type}</TableCell>
                  <TableCell>{decl.product_description?.substring(0, 50)}{decl.product_description?.length > 50 ? '...' : ''}</TableCell>
                  <TableCell>{decl.hs_code || '-'}</TableCell>
                  <TableCell>{decl.origin_country || '-'}</TableCell>
                  <TableCell>{decl.valid_until || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={decl.status}
                      color={getStatusColor(decl.status) as any}
                      sx={getStatusChipSx(decl.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {decl.status === 'pending' && (
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          sx={actionBtnSx}
                          startIcon={confirmingId === decl.id ? <CircularProgress size={14} color="inherit" /> : <ConfirmIcon />}
                          onClick={() => handleDirectConfirm(decl.id)}
                          disabled={confirmingId === decl.id}
                        >
                          Confirm
                        </Button>
                      )}
                      {decl.status === 'requested' && (
                        <Button
                          size="small"
                          variant="contained"
                          sx={{
                            ...actionBtnSx,
                            backgroundColor: '#FBC02D',
                            color: '#5A4400',
                            '&:hover': { backgroundColor: '#F5B800' },
                          }}
                          startIcon={receivingId === decl.id ? <CircularProgress size={14} color="inherit" /> : <ReceivedIcon />}
                          onClick={() => handleMarkReceived(decl.id)}
                          disabled={receivingId === decl.id}
                        >
                          Mark Received
                        </Button>
                      )}
                      {(decl.status === 'expired' || decl.status === 'expiring_soon') && (
                        <Button
                          size="small"
                          variant="contained"
                          color="warning"
                          sx={actionBtnSx}
                          startIcon={renewingId === decl.id ? <CircularProgress size={14} color="inherit" /> : <EmailIcon />}
                          onClick={() => handleSendRenewal(decl.id)}
                          disabled={renewingId === decl.id}
                        >
                          Send Renewal
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="outlined"
                        sx={actionBtnSx}
                        startIcon={<UploadIcon />}
                        onClick={() => handleUploadClick(decl.id)}
                      >
                        Upload
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        sx={actionBtnSx}
                        startIcon={<HistoryIcon />}
                        onClick={() => handleViewHistory(decl.id)}
                      >
                        History
                      </Button>
                      {decl.hs_code && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          sx={actionBtnSx}
                          startIcon={<ComplianceIcon />}
                          onClick={() => setComplianceDecl(decl)}
                        >
                          Compliance
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {declarations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No declarations found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Declarations</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedIds.size} declaration(s)? This will also remove all related alerts, version history, and email logs. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDeleteSelected} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting...' : `Delete ${selectedIds.size} Declaration(s)`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* File Upload Modal */}
      {selectedDeclarationId && (
        <FileUploadModal
          open={uploadModalOpen}
          declarationId={selectedDeclarationId}
          onClose={handleUploadModalClose}
          onUploadSuccess={handleUploadSuccess}
        />
      )}

      {/* Extraction Result Modal */}
      {selectedDeclarationId && extractionResult && (
        <ExtractionResultModal
          open={extractionModalOpen}
          declarationId={selectedDeclarationId}
          extractionResult={extractionResult}
          onClose={handleExtractionModalClose}
          onConfirm={handleConfirmSuccess}
        />
      )}

      {/* Version History Modal */}
      {selectedHistoryDeclarationId && (
        <VersionHistoryModal
          open={historyModalOpen}
          declarationId={selectedHistoryDeclarationId}
          onClose={handleHistoryModalClose}
        />
      )}

      {/* Invoice Analysis Modal */}
      <InvoiceAnalysisModal
        open={invoiceAnalysisOpen}
        onClose={() => setInvoiceAnalysisOpen(false)}
        onSaved={() => loadData(true)}
      />

      {/* Compliance Check Modal */}
      <Dialog
        open={!!complianceDecl}
        onClose={() => setComplianceDecl(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ComplianceIcon color="warning" />
            <span>Compliance Check — {complianceDecl?.supplier_name}</span>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Checking what declarations are required for HS <strong>{complianceDecl?.hs_code}</strong>
              {complianceDecl?.origin_country ? ` from ${complianceDecl.origin_country}` : ''}.
            </Typography>
            {complianceDecl && (
              <ComplianceCheckPanel
                hsCode={complianceDecl.hs_code}
                originCountry={complianceDecl.origin_country}
                productDescription={complianceDecl.product_description}
                supplierId={complianceDecl.supplier_id}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setComplianceDecl(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeclarationsTab;
